import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import { ComegoTime, FeedEntry, Time } from "@timeghost/web-api";
import { BehaviorSubject } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";
import { TimerService } from "../../timer-calendar-view.service";
import { resolveSegmentEndTime, resolveSegmentStartTime } from "../timer-calendar-view.mapper";
import {
	CalendarDayViewModel,
	CalendarEnabledTimes,
	HoursSegmentPresets,
	SegmentRangeFill,
} from "../timer-calendar-view.models";

function getSelectionBounds(selection: Set<number>): {
	first: number | undefined;
	last: number | undefined;
} {
	if (selection.size === 0) return { first: undefined, last: undefined };
	const indices = [...selection];
	return { first: Math.min(...indices), last: Math.max(...indices) };
}

function getProjectSelectionBoundsByRow(
	selection: Map<number, Set<number>>
): Map<number, { first: number | undefined; last: number | undefined }> {
	const bounds = new Map<number, { first: number | undefined; last: number | undefined }>();
	for (const [rowIndex, set] of selection.entries()) {
		bounds.set(rowIndex, getSelectionBounds(set));
	}
	return bounds;
}

function projectBoundsFingerprint(m: Map<number, { first: number | undefined; last: number | undefined }>): string {
	return [...m.entries()].map(([row, b]) => `${row}:${b.first ?? ""}:${b.last ?? ""}`).join("|");
}

export interface ProjectPointerDownEvent {
	rowIndex: number;
	segIndex: number;
	event: PointerEvent;
	time?: Time;
}

export interface ProjectPointerMoveEvent {
	rowIndex: number;
	event: PointerEvent;
}

export interface ProjectFillClickEvent {
	timeRef?: Time;
	feedRef?: FeedEntry;
}

export interface WorkingPointerDownEvent {
	segIndex: number;
	event: PointerEvent;
	comegoRef?: ComegoTime;
	fill?: SegmentRangeFill;
}

export interface WorkingFillClickEvent {
	fill: SegmentRangeFill;
}

@Component({
	selector: "app-calendar-timer-day-segments",
	templateUrl: "./calendar-timer-day-segments.component.html",
	styleUrls: ["./calendar-timer-day-segments.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: "block h-full min-h-0 w-full" },
})
export class CalendarTimerDaySegmentsComponent {
	@Input({ required: true }) day!: CalendarDayViewModel;
	@Input({ required: true }) enabledTimes!: CalendarEnabledTimes;
	@Input() segmentWidthPx = 75;
	@Input() timelineWidthPx = 0;
	@Input() hourLabelOffsetPx = 27.5;
	@Input() dayStartHour = 0;
	@Input() segmentsPerHour: HoursSegmentPresets = 2;

	private _workingSelection = new Set<number>();
	private readonly workingSelectionSubject = new BehaviorSubject<Set<number>>(this._workingSelection);

	@Input()
	set workingSelection(value: Set<number>) {
		this._workingSelection = value;
		this.workingSelectionSubject.next(value);
	}
	get workingSelection(): Set<number> {
		return this._workingSelection;
	}

	readonly selectionBounds$ = this.workingSelectionSubject.pipe(
		map(getSelectionBounds),
		distinctUntilChanged((a, b) => a.first === b.first && a.last === b.last),
		shareReplay(1)
	);

	private _projectSelection = new Map<number, Set<number>>();
	private readonly projectSelectionSubject = new BehaviorSubject<Map<number, Set<number>>>(this._projectSelection);
	@Input()
	set projectSelection(value: Map<number, Set<number>>) {
		this._projectSelection = value;
		this.projectSelectionSubject.next(value);
	}
	get projectSelection(): Map<number, Set<number>> {
		return this._projectSelection;
	}

	readonly projectSelectionBoundsByRow$ = this.projectSelectionSubject.pipe(
		map(getProjectSelectionBoundsByRow),
		distinctUntilChanged((a, b) => projectBoundsFingerprint(a) === projectBoundsFingerprint(b)),
		shareReplay(1)
	);
	@Input() draggingProjectColor?: string;
	@Input() draggingWorkingStyle?: Record<string, string>;

	@Output() projectPointerDown = new EventEmitter<ProjectPointerDownEvent>();
	@Output() projectPointerMove = new EventEmitter<ProjectPointerMoveEvent>();
	@Output() projectFillClick = new EventEmitter<ProjectFillClickEvent>();
	@Output() workingPointerDown = new EventEmitter<WorkingPointerDownEvent>();
	@Output() workingPointerMove = new EventEmitter<PointerEvent>();
	@Output() workingFillClick = new EventEmitter<WorkingFillClickEvent>();
	@Output() projectFillContextEdit = new EventEmitter<Time>();
	@Output() projectFillContextDuplicate = new EventEmitter<Time>();
	@Output() projectFillContextRemove = new EventEmitter<Time>();

	@ViewChild("eventContextTrigger", { static: true })
	contextMenu!: MatMenuTrigger;

	contextMenuPosition = { x: "0px", y: "0px" };

	readonly canCreateWorkinghours = this.service.workinghoursCanCreate;
	readonly canUpdateWorkinghours = this.service.workinghoursCanUpdate;

	constructor(private service: TimerService) {}

	onProjectFillContextMenu(event: MouseEvent, fill: SegmentRangeFill): void {
		return;
		if (!fill.timeRef || fill.feedRef) return;
		event.preventDefault();
		event.stopPropagation();
		this.contextMenuPosition.x = event.clientX + "px";
		this.contextMenuPosition.y = event.clientY + "px";
		this.contextMenu.menuData = { time: fill.timeRef };
		this.contextMenu.menu?.focusFirstItem("mouse");
		this.contextMenu.openMenu();
	}

	isProjectFillContextDisabled(time: Time | null | undefined): boolean {
		return !!time?.project?.completed;
	}

	isProjectSegmentSelected(rowIndex: number, segIndex: number): boolean {
		return (this.projectSelection.get(rowIndex)?.has(segIndex) ?? false) && !this.draggingProjectColor;
	}

	getProjectSegmentSelectedStyle(rowIndex: number, segIndex: number): Record<string, string> | null {
		if (!this.projectSelection.get(rowIndex)?.has(segIndex) || !this.draggingProjectColor) return null;
		return { backgroundColor: this.draggingProjectColor, color: "white" };
	}

	isWorkingSegmentSelected(segIndex: number): boolean {
		return this.workingSelection.has(segIndex) && !this.draggingWorkingStyle;
	}

	segmentStartTime(index: number): Date {
		return resolveSegmentStartTime(this.day.date, index, this.dayStartHour, this.segmentsPerHour);
	}

	segmentEndTime(index: number): Date {
		return resolveSegmentEndTime(this.day.date, index, this.dayStartHour, this.segmentsPerHour);
	}

	projectSelectionBounds(rowIndex: number): { first: number | undefined; last: number | undefined } {
		return getSelectionBounds(this.projectSelection.get(rowIndex) ?? new Set());
	}

	isProjectSelectionSingleLabel(rowIndex: number, segIndex: number): boolean {
		const { first, last } = this.projectSelectionBounds(rowIndex);
		return first != null && first === last && first === segIndex;
	}

	isProjectSelectionStartLabel(rowIndex: number, segIndex: number): boolean {
		const { first, last } = this.projectSelectionBounds(rowIndex);
		return first != null && first !== last && first === segIndex;
	}

	isProjectSelectionEndLabel(rowIndex: number, segIndex: number): boolean {
		const { first, last } = this.projectSelectionBounds(rowIndex);
		return last != null && first !== last && last === segIndex;
	}

	workingSelectionBounds(): { first: number | undefined; last: number | undefined } {
		return getSelectionBounds(this.workingSelection);
	}

	isWorkingSelectionSingleLabel(segIndex: number): boolean {
		const { first, last } = this.workingSelectionBounds();
		return first != null && first === last && first === segIndex;
	}

	isWorkingSelectionStartLabel(segIndex: number): boolean {
		const { first, last } = this.workingSelectionBounds();
		return first != null && first !== last && first === segIndex;
	}

	isWorkingSelectionEndLabel(segIndex: number): boolean {
		const { first, last } = this.workingSelectionBounds();
		return last != null && first !== last && last === segIndex;
	}

	getWorkingSegmentSelectedStyle(segIndex: number): Record<string, string> | null {
		if (!this.workingSelection.has(segIndex) || !this.draggingWorkingStyle) return null;
		return this.draggingWorkingStyle;
	}

	isWorkingFillSelected(fill: SegmentRangeFill): boolean {
		if (this.draggingWorkingStyle) return false;
		for (let seg = fill.rangeFirst; seg <= fill.rangeLast; seg++) {
			if (this.workingSelection.has(seg)) return true;
		}
		return false;
	}

	isProjectFillSelected(rowIndex: number, fill: SegmentRangeFill): boolean {
		if (this.draggingProjectColor) return false;
		const selection = this.projectSelection.get(rowIndex);
		if (!selection) return false;
		for (let seg = fill.rangeFirst; seg <= fill.rangeLast; seg++) {
			if (selection.has(seg)) return true;
		}
		return false;
	}

	onSuggestionFillClick(fill: SegmentRangeFill, event: MouseEvent): void {
		if (!fill.isSuggestion) return;
		if (!this.canCreateWorkinghours.value) return;
		event.stopPropagation();
		this.workingFillClick.emit({ fill });
	}
}
